import { Component, ChangeDetectionStrategy, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, merge } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { CommunicationTemplateService } from '../../services/communication-template.service';
import {
  CommunicationTemplateDTO,
  CommunicationTypeOptions,
  EntityState,
  getCommunicationTypeLabel
} from '../../core/models';

@Component({
  selector: 'app-communication-templates-list',
  templateUrl: './communication-templates-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .communication-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .communication-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    ReactiveFormsModule,
    TableModule,
    DrawerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    MenuModule
  ]
})
export class CommunicationTemplatesListComponent {
  private service = inject(CommunicationTemplateService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  @ViewChild('rowMenu') rowMenu!: Menu;

  templates = signal<CommunicationTemplateDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  pageSizeOptions = [20, 50, 100];
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  rowMenuItems: MenuItem[] = [];
  private selectedTemplate: CommunicationTemplateDTO | null = null;

  readonly typeOptions = [{ value: 'ALL', label: 'Todos' }, ...CommunicationTypeOptions];
  readonly stateOptions = [
    { value: 'ALL', label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];
  readonly getCommunicationTypeLabel = getCommunicationTypeLabel;

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    q: [''],
    type: [this.allFilterValue],
    state: [this.allFilterValue]
  });

  hasActiveFilters = signal(false);

  constructor() {
    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    merge(
      this.filtersForm.controls.type.valueChanges,
      this.filtersForm.controls.state.valueChanges
    ).pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());

    this.loadTemplates();
  }

  loadTemplates(): void {
    this.loading.set(true);
    this.error.set(null);
    const spec = this.buildSpecFromFilters();
    const pageable = { page: this.currentPage(), size: this.pageSize(), sort: ['name,asc'] };

    this.service.search(spec, pageable).subscribe({
      next: (response) => {
        if (response.ok) {
          this.templates.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems);
            this.pageSize.set(response.page.pageSize);
            this.currentPage.set(Math.max(0, response.page.currentPage - 1));
          }
        } else {
          this.error.set(response.message || 'Error al cargar plantillas');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: () => {
        this.error.set('Error al conectar con el servidor.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  onLazyLoad(event: { first: number; rows: number }): void {
    const page = event.rows ? Math.floor(event.first / event.rows) : 0;
    const size = event.rows || this.pageSize();
    if (page === this.currentPage() && size === this.pageSize()) return;
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadTemplates();
  }

  applyFilters(): void {
    const { q, type, state } = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(q?.trim() || (type && type !== this.allFilterValue) || (state && state !== this.allFilterValue))
    );
    this.currentPage.set(0);
    this.loadTemplates();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      q: '',
      type: this.allFilterValue,
      state: this.allFilterValue
    });
    this.hasActiveFilters.set(false);
    this.currentPage.set(0);
    this.loadTemplates();
  }

  openRowMenu(event: Event, template: CommunicationTemplateDTO): void {
    event.stopPropagation();
    this.selectedTemplate = template;
    this.rowMenuItems = [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => {
          if (this.selectedTemplate?.id != null) {
            void this.router.navigate(['/comunicacion', this.selectedTemplate.id]);
          }
        }
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        command: () => this.deleteTemplate(this.selectedTemplate)
      }
    ];
    this.rowMenu.toggle(event);
  }

  editTemplate(template: CommunicationTemplateDTO, event?: Event): void {
    event?.stopPropagation();
    if (template.id != null) {
      void this.router.navigate(['/comunicacion', template.id]);
    }
  }

  deleteTemplate(template: CommunicationTemplateDTO | null): void {
    if (!template?.id) return;
    if (!confirm(`¿Eliminar la plantilla "${template.name}"?`)) return;

    this.service.delete(template.id).subscribe({
      next: (res) => {
        if (res.ok) {
          this.toastr.success('Plantilla eliminada');
          this.loadTemplates();
        } else {
          this.toastr.error(res.message || 'No se pudo eliminar');
        }
      },
      error: (err) => this.toastr.error(err.error?.message || 'Error al eliminar')
    });
  }

  getStateSeverity(state?: string): 'success' | 'danger' | 'secondary' | undefined {
    if (state === EntityState.ACTIVE) return 'success';
    if (state === EntityState.INACTIVE) return 'danger';
    return 'secondary';
  }

  private buildSpecFromFilters(): Record<string, unknown> {
    const { q, type, state } = this.filtersForm.getRawValue();
    const spec: Record<string, unknown> = {};
    if (q?.trim()) spec['q[lk]'] = q.trim();
    if (type && type !== this.allFilterValue) spec['type'] = type;
    if (state && state !== this.allFilterValue) spec['state'] = state;
    return spec;
  }
}
